import React from 'react';
import { Chip, Button } from '@heroui/react';
import { FileText, Pencil, Trash2, Paperclip } from 'lucide-react';
import { expiryState } from '../documentDates.js';
import { formatUserDate } from '../formatUserDate.js';

/**
 * DocumentsTable — the document list.
 *
 * A plain semantic table, not HeroUI's: `react-heroui-layout.twig` zeroes
 * padding and borders on any `<th>`/`<td>` that lacks a `role`, so the roles
 * below are load-bearing, not decoration. HeroUI's Table was also avoided
 * because its collection API desyncs when columns vary — not needed here, and
 * a plain table keeps the row actions trivial.
 */
function DocumentsTable({ documents, typeName, onView, onEdit, onDelete, busyId }) {
	if (!documents.length) {
		return (
			<div className="bg-content1 border border-default-200 rounded-xl py-14 px-6 text-center">
				<p className="text-[15px] font-semibold text-foreground m-0">No documents yet</p>
				<p className="text-sm text-default-500 mt-1 mb-0">
					Add a licence, medical or insurance document to keep it on file.
				</p>
			</div>
		);
	}

	return (
		<>
			{/* Phone: cards — the table's six columns cannot fit without a sideways scroll. */}
			{/* Hidden on a wrapper: the layout forces `.flex { display: flex !important }`,
			    which would beat `md:hidden` on the list itself. */}
			<div className="md:hidden">
				<ul className="list-none p-0 m-0 flex flex-col gap-2">
					{documents.map(doc => {
						const exp = expiryState(doc.dateExpires);
						const scans = Number(doc.imagesAmount || 0) + Number(doc.filesAmount || 0);
						return (
							<li key={doc.id}>
								<button
									type="button"
									onClick={() => onView(doc)}
									className="w-full text-left bg-content1 border border-default-200 rounded-xl px-4 py-3 flex flex-col gap-1.5 active:bg-default-50"
								>
									<span className="flex items-start justify-between gap-2">
										<span className="text-[14px] font-medium text-foreground break-words">
											{doc.name || 'Untitled'}
										</span>
										{exp ? (
											<Chip size="sm" variant="flat" color={exp.tone} className="shrink-0">
												{exp.label}
											</Chip>
										) : null}
									</span>
									<span className="text-[13px] text-default-500 break-words">
										{[typeName(doc.type), doc.number].filter(Boolean).join(' · ') || '—'}
									</span>
									<span className="flex items-center justify-between gap-2 text-[13px]">
										<span className="text-default-500">
											{doc.dateExpires ? `Expires ${formatUserDate(doc.dateExpires)}` : 'No expiration'}
										</span>
										{scans > 0 ? (
											<span className="inline-flex items-center gap-1 text-default-500">
												<Paperclip className="w-3.5 h-3.5" aria-hidden="true" />
												{scans}
											</span>
										) : (
											<span className="text-danger">None attached</span>
										)}
									</span>
								</button>
							</li>
						);
					})}
				</ul>
			</div>

			{/* `max-md:hidden`, not `hidden md:block` — Bootstrap's `.hidden` is !important. */}
			<div className="max-md:hidden bg-content1 border border-default-200 rounded-xl overflow-hidden">
				<div>
					{/* `table-fixed` + a colgroup, NOT a min-width floor: the roomier
					    cells the design asks for pushed a min-width table past the
					    column and produced the horizontal scroll the responsive
					    contract forbids. Fixed layout lets long values truncate
					    inside their cell instead of widening the table. */}
					<table className="w-full border-collapse table-fixed">
						<colgroup>
							<col className="w-[24%]" />
							<col className="w-[18%]" />
							<col className="w-[13%]" />
							<col className="w-[21%]" />
							<col className="w-[13%]" />
							<col className="w-[11%]" />
						</colgroup>
						<thead>
							<tr className="bg-default-100">
								{['Document Name', 'Type', 'Number', 'Expires', 'Files', 'Actions'].map(h => (
									<th
										key={h}
										role="columnheader"
										className="text-left text-[12px] font-semibold tracking-wide text-default-500 px-4 py-3.5 border-b border-default-200"
									>
										{h}
									</th>
								))}
							</tr>
						</thead>
						<tbody>
							{documents.map(doc => {
								const exp = expiryState(doc.dateExpires);
								const images = Number(doc.imagesAmount || 0);
								const files = Number(doc.filesAmount || 0);
								return (
									<tr
										key={doc.id}
										tabIndex={0}
										onClick={() => onView(doc)}
										onKeyDown={e => {
											if (e.key === 'Enter') onView(doc);
										}}
										className="border-b border-default-200 last:border-b-0 cursor-pointer hover:bg-default-50 focus-visible:outline-2 focus-visible:outline-primary"
									>
										<td role="cell" className="px-4 py-4 align-middle">
											<span className="inline-flex items-center gap-2 min-w-0">
												<FileText className="w-4 h-4 shrink-0 text-default-400" aria-hidden="true" />
												<span className="text-[14px] font-medium text-foreground truncate">
													{doc.name || 'Untitled'}
												</span>
											</span>
										</td>
										<td role="cell" className="px-4 py-4 text-[14px] text-default-500 align-middle">
											<span className="block truncate">{typeName(doc.type) || '—'}</span>
										</td>
										<td role="cell" className="px-4 py-4 text-[14px] text-default-500 align-middle">
											<span className="block truncate">{doc.number || '—'}</span>
										</td>
										<td role="cell" className="px-4 py-4 align-middle">
											{exp ? (
												<span className="flex flex-wrap items-center gap-x-2 gap-y-1">
													<span className="text-[14px] text-default-500">
														{formatUserDate(doc.dateExpires)}
													</span>
													<Chip size="sm" variant="flat" color={exp.tone}>
														{exp.label}
													</Chip>
												</span>
											) : (
												<span className="text-[14px] text-default-400">No expiration</span>
											)}
										</td>
										<td role="cell" className="px-4 py-4 align-middle">
											{images + files > 0 ? (
												<span className="inline-flex items-center gap-1.5 text-[13px] text-default-500">
													<Paperclip className="w-3.5 h-3.5" aria-hidden="true" />
													{images} / {files}
												</span>
											) : (
												// Legacy flagged an attachment-less document in red; keep the
												// signal, drop the bare icon in favour of readable text.
												<span className="text-[13px] text-danger">None attached</span>
											)}
										</td>
										{/* The row opens the preview; its buttons must not. */}
										<td
											role="cell"
											className="px-4 py-4 align-middle"
											onClick={e => e.stopPropagation()}
											onKeyDown={e => e.stopPropagation()}
										>
											<div className="flex items-center gap-1">
												<Button
													isIconOnly
													size="sm"
													variant="light"
													aria-label={`Edit ${doc.name || 'document'}`}
													onPress={() => onEdit(doc)}
												>
													<Pencil className="w-4 h-4" />
												</Button>
												<Button
													isIconOnly
													size="sm"
													variant="light"
													color="danger"
													isLoading={busyId === doc.id}
													aria-label={`Delete ${doc.name || 'document'}`}
													onPress={() => onDelete(doc)}
												>
													<Trash2 className="w-4 h-4" />
												</Button>
											</div>
										</td>
									</tr>
								);
							})}
						</tbody>
					</table>
				</div>
			</div>
		</>
	);
}

export default DocumentsTable;
